<template>
  <div>
    <div v-if="loadingForm">
      <px-circular-loading/>
    </div>

    <v-container v-if="!loadingForm">
      <v-row v-if="isComprador">
        <v-col cols="12">
          <px-autocomplete-comprador
            required
            class="required"
            :error="$v.cotacao.company.$error"
            @blur="$v.cotacao.company.$touch()"
            v-model="cotacao.company"/>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" sm="4">
          <v-text-field
            outlined
            hide-details
            autocomplete="off"
            label="CPF/CNPJ coleta"
            v-mask="['###.###.###-##', '##.###.###/####-##']"
            :error="$v.cotacao.cpfCnpjColeta.$error"
            @blur="$v.cotacao.cpfCnpjColeta.$touch()"
            v-model="cotacao.cpfCnpjColeta"
          />
        </v-col>
        <v-col cols="12" sm="4">
          <v-text-field
            outlined
            hide-details
            autocomplete="off"
            label="CPF/CNPJ entrega"
            v-mask="['###.###.###-##', '##.###.###/####-##']"
            :error="$v.cotacao.cpfCnpjEntrega.$error"
            @blur="$v.cotacao.cpfCnpjEntrega.$touch()"
            v-model="cotacao.cpfCnpjEntrega"
          />
        </v-col>
        <v-col cols="12" sm="4">
          <px-select-enum v-model="cotacao.localPagamento"
                          :items="locaisPagamento"
                          :error="$v.cotacao.localPagamento.$error"
                          @blur="$v.cotacao.localPagamento.$touch()"
                          class="required"
                          label="Pagador"/>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" sm="6">
          <px-input-cep-cidade label="CEP de coleta"
                               class="required"
                               v-model="cotacao.cepColeta"
                               :error="$v.cotacao.cepColeta.$error"
                               @blur="$v.cotacao.cepColeta.$touch()"
                               :initial-municipio="initialMunicipioColeta"
                               :disabled="isEditing"
                               @loading="setLoading"
                               @cidadeloaded="onLoadCidade"
                               @change="onChangeCepColeta"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <px-input-cep-cidade label="CEP de entrega"
                               class="required"
                               v-model="cotacao.cepEntrega"
                               :error="$v.cotacao.cepEntrega.$error"
                               @blur="$v.cotacao.cepEntrega.$touch()"
                               :initial-municipio="initialMunicipioEntrega"
                               :disabled="isEditing"
                               @loading="setLoading"
                               @cidadeloaded="onLoadCidadeEntrega"/>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" sm="6" md="3">
          <px-input-number label="Peso total (kg)"
                           required
                           class="required"
                           :error="$v.cotacao.pesoTotal.$error"
                           @blur="$v.cotacao.pesoTotal.$touch()"
                           v-model="cotacao.pesoTotal"/>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <px-input-money
            ref="precoPraticado"
            class="required"
            label="Valor da mercadoria (R$)"
            :error="$v.cotacao.valorMercadoria.$error"
            @blur="$v.cotacao.valorMercadoria.$touch()"
            v-model="cotacao.valorMercadoria"/>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <px-input-number label="Quantidade de notas fiscais"
                           required
                           class="required"
                           :error="$v.cotacao.quantidadeNotas.$error"
                           @blur="$v.cotacao.quantidadeNotas.$touch()"
                           v-model="cotacao.quantidadeNotas"/>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-text-field outlined
                        hide-details
                        maxlength="100"
                        class="required"
                        label="Tipo de mercadoria"
                        :error="$v.cotacao.tipoMercadoria.$error"
                        @blur="$v.cotacao.tipoMercadoria.$touch()"
                        v-model="cotacao.tipoMercadoria"/>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-textarea
            outlined
            rows="1"
            label="Observações"
            counter="1500"
            v-model="cotacao.observacao"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-subheader class="body-1">
            <v-icon>
              mdi-package-variant
            </v-icon>
            <span class="ml-2">Volumes</span>
          </v-subheader>
          <v-divider></v-divider>
        </v-col>
      </v-row>
      <v-row v-if="!cotacao.volumes.length">
        <v-col cols="12" class="body-1 mb-5 ml-2" align="center">
          Não há volumes nessa cotação
        </v-col>
      </v-row>
      <v-row v-for="(volume, i) in cotacao.volumes" :key="i">
        <v-col cols="12" sm="3">
          <px-input-number label="Quantidade"
                           class="required"
                           required
                           dense
                           :error="$v.cotacao.volumes.$each[i].quantidade.$error"
                           @blur="$v.cotacao.volumes.$each[i].quantidade.$touch()"
                           v-model="volume.quantidade"/>
        </v-col>
        <v-col cols="12" sm="3">
          <px-input-percentage
            dense
            label="Largura (m)"
            suffix=" m"
            class="required"
            :is-percentage="false"
            :maxlength="10"
            :error="$v.cotacao.volumes.$each[i].largura.$error"
            @blur="$v.cotacao.volumes.$each[i].largura.$touch()"
            v-model="volume.largura"/>
        </v-col>
        <v-col cols="12" sm="3">
          <px-input-percentage
            dense
            label="Comprimento (m)"
            suffix=" m"
            class="required"
            :is-percentage="false"
            :maxlength="10"
            :error="$v.cotacao.volumes.$each[i].comprimento.$error"
            @blur="$v.cotacao.volumes.$each[i].comprimento.$touch()"
            v-model="volume.comprimento"/>
        </v-col>
        <v-col cols="12" sm="3">
          <px-input-percentage
            dense
            label="Altura (m)"
            suffix=" m"
            class="required"
            :is-percentage="false"
            :maxlength="10"
            append-outer-icon="mdi-close"
            v-model="volume.altura"
            :error="$v.cotacao.volumes.$each[i].altura.$error"
            @blur="$v.cotacao.volumes.$each[i].altura.$touch()"
            @click:append-outer="removeVolume(i)"/>
        </v-col>
      </v-row>
      <v-row dense>
        <v-spacer></v-spacer>
        <v-btn small color="primary" @click="addVolume">
          <v-icon small>mdi-plus</v-icon>
          Volume
        </v-btn>
      </v-row>
    </v-container>
    <v-btn color="primary"
           @click="onClickNext">
      {{ btnLabel }}
    </v-btn>
  </div>
</template>

<script>
import { required, requiredIf } from 'vuelidate/lib/validators';
import validationMixin from '@/mixins/validation-mixin';
import { onlyNumbers } from '@/utils/regex-utils';
import { isValid as isValidCnpj } from '@/utils/validate-cnpj';
import { LocalPagamentoFrete } from 'px-business-components';
import { isValid as isValidCpf } from '@/utils/validate-cpf';
import PxInputCepCidade from '../PxInputCepCidade.vue';
import CompradorService from '../comprador-service';
import FornecedorService from '../fornecedor-service';

const defaultVolume = () => ({
  quantidade: null,
  largura: null,
  comprimento: null,
  altura: null,
});

function validateCnpj(value) {
  if (!value) {
    return true;
  }
  const valor = onlyNumbers(value);
  if (valor.length <= 11) {
    return isValidCpf(value);
  }
  return isValidCnpj(value);
}

export default {
  mixins: [validationMixin],
  props: {
    btnLabel: {
      type: String,
      default: 'Continuar',
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    isCopy: {
      type: Boolean,
      default: false,
    },
    isComprador: {
      type: Boolean,
      default: true,
    },
    cotacao: Object,
  },
  data() {
    return {
      type: this.$route.query.type,
      loadingForm: false,
      initialMunicipioColeta: null,
      initialMunicipioEntrega: null,
      loadingCidade: false,
      cidadeOrigem: 'Informe o CEP',
      locaisPagamento: LocalPagamentoFrete.values,
    };
  },
  methods: {
    onClickNext() {
      if (this.validateForm()) {
        return;
      }
      this.$emit('next');
    },
    getFaturamento() {
      return CompradorService.getDefaultFaturamento()
        .then(({ data }) => {
          this.cotacao.company = data;
          this.cotacao.cpfCnpjEntrega = data?.pessoa?.cnpj;
        });
    },
    getEntrega() {
      return CompradorService.getDefaultEntrega()
        .then(({ data }) => {
          if (data) {
            this.cotacao.cepEntrega = data?.endereco?.cep;
            this.cotacao.municipioEntrega = {
              ...data?.endereco?.municipio,
              estado: data?.endereco?.estado,
            };
            this.initialMunicipioEntrega = this.cotacao.municipioEntrega;
          }
        });
    },
    getCompany() {
      if (this.isCopy) {
        return;
      }
      this.loadingForm = true;
      Promise.all([
        this.getFaturamento(),
        this.getEntrega(),
      ])
        .finally(() => {
          this.loadingForm = false;
        });
    },
    getFornecedor() {
      if (this.isCopy) {
        return;
      }
      this.loadingForm = true;
      FornecedorService.get()
        .then(({ data }) => {
          this.cotacao.company = data;
        })
        .finally(() => {
          this.loadingForm = false;
        });
    },
    addVolume() {
      this.cotacao.volumes.push(defaultVolume());
    },
    removeVolume(index) {
      this.cotacao.volumes.splice(index, 1);
    },
    onChangeCepColeta() {
      this.cotacao.transportadoras = [];
    },
    onLoadCidade(cidade) {
      this.cotacao.municipioColeta = cidade;
    },
    onLoadCidadeEntrega(cidade) {
      this.cotacao.municipioEntrega = cidade;
    },
    setLoading(loading) {
      this.loadingCidade = loading;
    },
  },
  created() {
    if (this.isComprador) {
      this.getCompany();
    } else {
      this.getFornecedor();
    }

    if (this.isEditing || this.isCopy) {
      this.initialMunicipioColeta = this.cotacao.municipioColeta;
      this.initialMunicipioEntrega = this.cotacao.municipioEntrega;
    }
  },
  components: {
    PxInputCepCidade,
  },
  validations: {
    cotacao: {
      company: {
        required,
      },
      cpfCnpjColeta: {
        validateCnpj,
      },
      cpfCnpjEntrega: {
        validateCnpj,
      },
      localPagamento: {
        required,
      },
      cepColeta: {
        required,
        coletaRequired(value, model) {
          return !!model.municipioColeta;
        },
      },
      municipioColeta: {
        required,
      },
      cepEntrega: {
        required,
        entregaRequired(value, model) {
          return !!model.municipioEntrega;
        },
      },
      municipioEntrega: {
        required,
      },
      valorMercadoria: {
        required,
        minValue(value) {
          return value > 0;
        },
      },
      quantidadeNotas: {
        required,
      },
      tipoMercadoria: {
        required,
      },
      pesoTotal: {
        required: requiredIf(function () {
          return !this.cotacao.volumes || !this.cotacao.volumes.length;
        }),
      },
      volumes: {
        $each: {
          quantidade: {
            required,
            minValue(value) {
              return value > 0;
            },
          },
          largura: {
            required,
            minValue(value) {
              return value > 0;
            },
          },
          comprimento: {
            required,
            minValue(value) {
              return value > 0;
            },
          },
          altura: {
            required,
            minValue(value) {
              return value > 0;
            },
          },
        },
      },
    },
  },
};
</script>
